import { useMemo } from 'react';
import { Button } from '@mui/material';
import { useUserContext } from 'shared/Providers/User';
import { isSharing } from 'shared/utils/webSettings';
import { getShareProperty } from 'shared/_model/sharing/utils';
import { Sharing, SharingType } from 'shared/_model/sharing/types';
import { DisabledIcon, IconShare, SuccessIcon } from 'shared/_ui/icon';

type TShareSettingsIconProps = {
	type: SharingType;
	sharing?: Sharing;
};

type TShareSettingsButtonProps = TShareSettingsIconProps & {
  disabled?: boolean;
  onlyIcon?: boolean;
  onClick?: () => void;
};

function getComponent(show: boolean, sharing: Sharing | undefined, type: SharingType) {
	if (!show) {
		return SuccessIcon;
	}
	return getShareProperty(sharing).filter((sh) => sh.type === type).length
		? SuccessIcon
		: DisabledIcon;
}

export function ShareSettingsIcon(props: TShareSettingsIconProps) {
	const { type, sharing } = props;

	const { permissions: { canUseCloud } } = useUserContext();

	const show = useMemo(() => canUseCloud && !isSharing(), [canUseCloud]);

	const Component = useMemo(() => getComponent(show, sharing, type), [sharing, type, show]);

	if (!show) {
		return null;
	}

	return <Component Component={IconShare} sx={(theme) => ({
		[theme.breakpoints.up('sm')]: {
			height: '40px',
			width: '40px',
		},
	})} />;
}

export function ShareSettingsButton(props: TShareSettingsButtonProps) {
	const {
		type,
		disabled = false,
		onlyIcon = false,
		onClick = () => undefined,
		sharing,
	} = props;

	const { permissions: { canUseCloud } } = useUserContext();

	const show = useMemo(() => canUseCloud && !isSharing(), [canUseCloud]);

	const Component = useMemo(() => getComponent(show, sharing, type), [sharing, type, show]);

	if (!show) {
		return null;
	}

	if (onlyIcon) {
		return <Component Component={IconShare} />;
	}

	return (
		<Button
			size="small"
			onClick={onClick}
			disabled={disabled}
			startIcon={<Component Component={IconShare} />}
		>
			Share
		</Button>
	);
}
